import type { FC } from 'react';

import classNames from 'classnames';

import type {
  AssetsApprovalBySchema,
  AssetsApprovalSchemaNullable,
} from '@iconik-io/iconik-api/src/services/assets';

import { FailureStatuses } from './statuses/failure_statuses';
import { InfoStatuses } from './statuses/info_statuses';
import {
  StatusTooltip,
  type StatusTooltipComponent,
} from './statuses/status_tooltip';
import { SuccessStatuses } from './statuses/success_statuses';

import styles from './approval_detailed_status_icon.module.css';

export type ApprovalDetailedStatusIconProps = {
  approval?: AssetsApprovalSchemaNullable | null;
  className?: string;
  /**
   * `overlay` renders colored icons with no background, for use on top of media
   * or inline where a chip would be too heavy, such as a list row.
   */
  variant?: 'default' | 'overlay';
  /** Icon size, only applied by the `overlay` variant. Defaults to `xs`. */
  size?: 'xs' | 's';
  /** The current user, so statuses they set or requested get the "by you" variant. */
  userId: string;
  /** Wraps each status icon in a tooltip; defaults to the UAD tooltip. */
  TooltipComponent?: StatusTooltipComponent;
};

const getApprovals = (
  approval: AssetsApprovalSchemaNullable | null,
  userId: string,
) => {
  const approvedBy: AssetsApprovalBySchema[] = [];
  const rejectedBy: AssetsApprovalBySchema[] = [];
  const requestedBy: AssetsApprovalSchemaNullable[] = [];
  let rejectedByUser = false;
  let approvedByUser = false;
  let requestedByUser = false;

  if (!approval || approval.status === null) {
    return {
      approvedBy,
      rejectedBy,
      requestedBy,
      approvedByUser,
      requestedByUser,
      rejectedByUser,
    };
  }

  if (approval.status === 'REQUESTED') {
    requestedBy.push(approval);
    if (approval.requested_by === userId) {
      requestedByUser = true;
    }
  }

  if (approval.approvals_by && approval.approvals_by.length > 0) {
    approval.approvals_by.forEach((item) => {
      if (item.status === 'APPROVED') {
        approvedBy.push(item);
        if (item.user === userId) {
          approvedByUser = true;
        }
      }
      if (item.status === 'NOT_APPROVED') {
        rejectedBy.push(item);
        if (item.user === userId) {
          rejectedByUser = true;
        }
      }
    });
  }
  return {
    approvedBy,
    rejectedBy,
    requestedBy,
    approvedByUser,
    requestedByUser,
    rejectedByUser,
  };
};

export const ApprovalDetailedStatusIcon: FC<
  ApprovalDetailedStatusIconProps
> = ({
  approval,
  className,
  variant = 'default',
  size = 'xs',
  userId,
  TooltipComponent = StatusTooltip,
}) => {
  if (!approval) {
    return null;
  }

  const {
    approvedBy,
    requestedBy,
    rejectedBy,
    requestedByUser,
    rejectedByUser,
    approvedByUser,
  } = getApprovals(approval, userId);

  return (
    <div
      className={classNames(
        styles.status_container,
        {
          [styles.overlay]: variant === 'overlay',
          [styles.size_s]: size === 's',
        },
        className,
      )}
    >
      <FailureStatuses
        rejectedBy={rejectedBy}
        rejectedByUser={rejectedByUser}
        StatusTooltip={TooltipComponent}
      />
      <SuccessStatuses
        approvedBy={approvedBy}
        approvedByUser={approvedByUser}
        StatusTooltip={TooltipComponent}
      />
      <InfoStatuses
        requestedBy={requestedBy}
        requestedByUser={requestedByUser}
        StatusTooltip={TooltipComponent}
      />
    </div>
  );
};
